import { DeleteIcon, AddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Grid,
  Icon,
  IconButton,
  Spacer,
  Text,
  useToast,
} from "@chakra-ui/react";
import { CreatableSelect, Select } from "chakra-react-select";
import { useEffect, useRef, useState } from "react";
import { FaCheck, FaFilePdf, FaMoneyBill, FaSpinner } from "react-icons/fa";
import { useAPI } from "@/contexts/APIContext";
import { useProfile } from "@/contexts/ProfileContext";
import { useUserData } from "@/contexts/UserDataContext";
import { SCHOLARSHIP_OPTIONS } from "@/utils/profile";
import { createSelectOptions } from "@/utils/ui/select-helper";
import { useEditProfileContext } from "./edit/shared";
import SimpleTable from "./view/SimpleTable";

const scholarshipOptions = createSelectOptions(SCHOLARSHIP_OPTIONS);

const ScholarshipIcon = ({ name, onClickLocked }) => {
  const { profile, isLocked } = useProfile();
  const api = useAPI();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const onDownload = async () => {
    setLoading(true);

    const { urls } = await api.get("profiles/download-scholarship-file", true, {
      id: profile.id,
      scholarship: name,
    });
    if (urls.length === 1) {
      window.open(urls[0], "_blank");
      toast({
        title: "Download File",
        isClosable: true,
        duration: 5000,
        status: "success",
        description: `If you don't see the download, please enable pop-ups in your browser settings.`,
      });
    } else {
      window.alert("Download the files here:\n\n" + urls.join("\n\n"));
    }
    setLoading(false);
  };

  const onClick = () => {
    if (isLocked) {
      onClickLocked();
    } else {
      onDownload();
    }
  };

  if (loading) {
    return <Icon as={FaSpinner} boxSize={4} color="gray" />;
  } else {
    return (
      <Button
        rightIcon={<Icon as={FaFilePdf} />}
        colorScheme="black"
        variant="outline"
        onClick={onClick}
      >
        View Application
      </Button>
    );
  }
};

export const ViewableScholarshipList = ({ onClickLocked }) => {
  const { profile } = useProfile();

  const renderData = {};
  for (const scholarship of profile.scholarships) {
    renderData[scholarship.name] = scholarship.hasFile ? (
      <ScholarshipIcon onClickLocked={onClickLocked} name={scholarship.name} />
    ) : (
      ""
    );
  }

  return <SimpleTable data={renderData} />;
};

const ScholarshipUploader = ({ scholarship, initialHasFile, onChange }) => {
  const formRef = useRef();
  const inputRef = useRef();
  const api = useAPI();
  const { currentUser } = useUserData();
  const [disabled, setDisabled] = useState(false);
  const [hasFile, setHasFile] = useState(initialHasFile);
  const [statusDetails, setStatusDetails] = useState();

  const onSubmitForm = async (e) => {
    setDisabled(true);
    if (!disabled && e.target.files.length > 0) {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append("theFiles", file);
      await api.post(
        "profiles/upload-scholarship-file/" + encodeURIComponent(scholarship),
        currentUser,
        formData,
      );
      setHasFile(true);
    }
    formRef.current?.reset();
    setDisabled(false);
  };

  useEffect(() => {
    if (!hasFile) {
      setStatusDetails("Click to upload");
    } else {
      setStatusDetails("File uploaded!");
    }
    onChange(hasFile);
  }, [hasFile]);

  return (
    <Button
      isDisabled={!scholarship}
      onClick={() => inputRef.current?.click()}
      leftIcon={<Icon as={FaFilePdf} />}
      colorScheme="blue"
    >
      {disabled ? "Uploading..." : statusDetails}
      <Box w={0} h={0} overflow="hidden">
        <form ref={formRef} method="post">
          <input
            ref={inputRef}
            type="file"
            onChange={onSubmitForm}
            accept="application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            multiple={false}
          />
        </form>
      </Box>
    </Button>
  );
};

export const EditableScholarshipList = () => {
  const { initialProfileData, update } = useEditProfileContext();
  const [scholarships, setScholarships] = useState(initialProfileData.scholarships ?? []);

  useEffect(() => update("scholarships", scholarships), [scholarships]);

  const onClickAdd = () => {
    setScholarships([...scholarships, { name: "", hasFile: false }]);
  };

  return (
    <Box>
      <Flex>
        <Spacer />
        <Button
          colorScheme="green"
          leftIcon={<AddIcon />}
          my={4}
          isDisabled={scholarships.length >= 10}
          onClick={onClickAdd}
        >
          Add Scholarship
        </Button>
      </Flex>
      {scholarships.length > 0 && (
        <Grid templateColumns="3fr 1fr min-content" rowGap={4} columnGap={2}>
          <Box>
            <Text fontSize="2xl">Scholarship Name</Text>
            <Text>
              (If your scholarship isn&apos;t on our list, just type it in and click Create.)
            </Text>
          </Box>
          <Box>
            <Text fontSize="2xl">Upload Files</Text>
            {/*<Text>(Optional)</Text>*/}
          </Box>
          <Box />
          {scholarships.map((scholarship, i) => {
            const onUpdateField = (fieldName, value) => {
              const newScholarships = [...scholarships];
              newScholarships[i][fieldName] = value;
              setScholarships(newScholarships);
            };
            const onDelete = () => {
              const newScholarships = [...scholarships];
              newScholarships.splice(i, 1);
              setScholarships(newScholarships);
            };
            return (
              <>
                <CreatableSelect
                  key={`name-${i}`}
                  value={{ label: scholarship.name, value: scholarship.name }}
                  options={scholarshipOptions}
                  placeholder="Scholarship Name"
                  variant="flushed"
                  onChange={(e) => onUpdateField("name", e.value)}
                />
                <ScholarshipUploader
                  scholarship={scholarship.name}
                  initialHasFile={scholarship.hasFile}
                  onChange={(hasFile) => onUpdateField("hasFile", hasFile)}
                />
                <IconButton
                  colorScheme="red"
                  variant="ghost"
                  key={`delete-${i}`}
                  icon={<DeleteIcon />}
                  onClick={onDelete}
                />
              </>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};
